// hooks/useInAppBanner.ts
import { useEditUserNotificationMutation } from 'queryHooks/notifications/useEditUserNotificationMutation'
import { useGetUsersNotifications } from 'queryHooks/notifications/useGetUsersNotifications'
import { useMemo, useState } from 'react'
import { Notification } from 'resources/types'

type Params = {
	blockedPaths?: (string | RegExp)[]
}

export function useInAppBanner({ blockedPaths = [] }: Params) {
	const [sessionDismissed, setSessionDismissed] = useState<Set<string>>(new Set())


	const { notifications } = useGetUsersNotifications({
		params: {
			sortBy: 'createdOn',
			sort: 'desc',
		},
	})

	const { mutateAsync: updateNotification } = useEditUserNotificationMutation()

	const candidate = useMemo(() => {
		const now = Date.now()

		const score = (n: Notification) => {
			const created = n.createdOn?.toMillis?.() ?? 0
			const kindBoost = n.kind === 'admin' ? 1 : 0 // admin > transactional
			return created * 10 + kindBoost
		}

		return notifications
			.filter(n => n.deliveryMethods?.includes('inApp'))
			.filter(n => !n.deletedAt)
			.filter(n => !n.readAt)
			.filter(n => !n.expiresAt || n.expiresAt.toMillis() > now)
			.filter(n => (n.persistent ? !sessionDismissed.has(n.id) : !n.dismissedAt))
			.sort((a, b) => score(b) - score(a))[0]
	}, [notifications, sessionDismissed])

	const markRead = (n: Notification) => updateNotification({ notificationID: n.id, read: true })

	const dismiss = (n: Notification) => {
		if (n.persistent) {
			// session-only dismiss
			setSessionDismissed(prev => new Set(prev).add(n.id))
			return
		}
		// durable dismiss
		return updateNotification({ notificationID: n.id, dismissed: true })
	}

	const shouldBlockOnPath = (path: string) => blockedPaths.some(p => (typeof p === 'string' ? path === p : p.test(path)))

	return { notification: candidate, markRead, dismiss, shouldBlockOnPath }
}
